import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { sortStatusesByCategory } from '../FlowchartView/utils/statusUtils';
import { calculateDropdownPosition, closeAllDropdowns, CLOSE_ALL_DROPDOWNS_EVENT, FLOWCHART_PAN_START_EVENT } from '../shared/utils/dropdownPositioning';

/**
 * Gauge icon for the dropdown button
 */
const GaugeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2v4" />
    <path d="m16.2 7.8 2.9-2.9" />
    <path d="M18 12h4" />
    <path d="m16.2 16.2 2.9 2.9" />
    <path d="M12 18v4" />
    <path d="m4.9 19.1 2.9-2.9" />
    <path d="M2 12h4" />
    <path d="m4.9 4.9 2.9 2.9" />
    <circle cx="12" cy="12" r="4" />
  </svg>
);

const ChevronIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

/**
 * StatusLimitsDropdown Component (formerly ColumnLimitsDropdown)
 * 
 * Dropdown to view and edit WIP limits for all statuses at once.
 * Shows each status with its current count and limit.
 * 
 * Used in both Flow and Kanban views - limits apply across all views.
 */
const StatusLimitsDropdown = ({ 
  statuses = [],
  tasks = [],
  onSetWipLimit,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRefs = useRef({});

  // Sort statuses by category for display
  const sortedStatuses = sortStatusesByCategory(statuses);

  // Count tasks per status
  const getTaskCount = useCallback((statusId) => {
    return tasks.filter(t => t.status === statusId).length;
  }, [tasks]);

  // Count how many statuses have limits set
  const limitCount = statuses.filter(s => s.wipLimit !== null && s.wipLimit !== undefined).length;

  // Calculate dropdown position with edge detection
  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition(calculateDropdownPosition(rect, 300));
    }
  };

  // Open dropdown
  const handleToggle = () => {
    if (!isOpen) {
      closeAllDropdowns(buttonRef.current); // Close any other open dropdowns first
      updatePosition();
    }
    setIsOpen(!isOpen);
    setEditingStatusId(null);
  };

  // Listen for global close events (when another dropdown opens or view zooms/pans)
  useEffect(() => {
    const handleCloseAll = () => {
      setIsOpen(false);
      setEditingStatusId(null);
    };
    window.addEventListener(CLOSE_ALL_DROPDOWNS_EVENT, handleCloseAll);
    window.addEventListener(FLOWCHART_PAN_START_EVENT, handleCloseAll);
    return () => {
      window.removeEventListener(CLOSE_ALL_DROPDOWNS_EVENT, handleCloseAll);
      window.removeEventListener(FLOWCHART_PAN_START_EVENT, handleCloseAll);
    };
  }, []);

  // Start editing a status limit
  const handleStartEdit = (statusId, currentLimit) => {
    setEditingStatusId(statusId);
    setEditValue(currentLimit !== null && currentLimit !== undefined ? String(currentLimit) : '');
    // Focus the input after render
    setTimeout(() => {
      inputRefs.current[statusId]?.focus();
      inputRefs.current[statusId]?.select();
    }, 0);
  };

  // Save the limit
  const handleSave = (statusId) => {
    const value = editValue.trim();
    if (value === '') {
      onSetWipLimit?.(statusId, null);
    } else {
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue) && numValue >= 0) {
        onSetWipLimit?.(statusId, numValue);
      }
    }
    setEditingStatusId(null);
  };

  // Handle key down in input
  const handleKeyDown = (e, statusId) => {
    if (e.key === 'Enter') {
      handleSave(statusId);
    } else if (e.key === 'Escape') {
      setEditingStatusId(null);
    }
  };

  // Clear a limit
  const handleClear = (statusId) => {
    onSetWipLimit?.(statusId, null);
    setEditingStatusId(null);
  };

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setIsOpen(false);
        setEditingStatusId(null);
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (editingStatusId) {
          setEditingStatusId(null);
        } else {
          setIsOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, editingStatusId]);

  return (
    <>
      <button
        ref={buttonRef}
        className={`toolbar-btn ${isOpen ? 'toolbar-btn--active' : ''} ${limitCount > 0 ? 'toolbar-btn--has-indicator' : ''}`}
        onClick={handleToggle}
        title="Configure per-status WIP limits"
      >
        <span className="toolbar-btn__spacer" />
        <span className="toolbar-btn__content">
          <span className="toolbar-btn__icon">
            <GaugeIcon />
          </span>
          <span className="toolbar-btn__label">Status Limits</span>
          {limitCount > 0 && (
            <span className="toolbar-btn__badge">{limitCount}</span>
          )}
        </span>
        <span className="toolbar-btn__chevron">
          <ChevronIcon />
        </span>
      </button>

      {isOpen && dropdownPosition && createPortal(
        <div
          ref={dropdownRef}
          className="column-limits-dropdown"
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            maxHeight: dropdownPosition.maxHeight,
            overflowY: 'auto',
            zIndex: 50000,
          }}
        >
          <div className="column-limits-dropdown__header">
            <span>Per-Status Limits</span>
            <span className="column-limits-dropdown__count">
              {limitCount} active
            </span>
          </div>

          <div className="column-limits-dropdown__body">
            {sortedStatuses.map(status => {
              const taskCount = getTaskCount(status.id);
              const hasLimit = status.wipLimit !== null && status.wipLimit !== undefined;
              const isAtLimit = hasLimit && taskCount === status.wipLimit;
              const isOverLimit = hasLimit && taskCount > status.wipLimit;
              const isBlocked = status.category === 'blocked';
              const isEditing = editingStatusId === status.id;

              return (
                <div 
                  key={status.id} 
                  className={`column-limits-dropdown__item ${isBlocked ? 'column-limits-dropdown__item--blocked' : ''}`}
                >
                  <span 
                    className="column-limits-dropdown__color"
                    style={{ backgroundColor: status.color }}
                  />
                  <span className="column-limits-dropdown__name">
                    {status.name}
                  </span>
                  <span className="column-limits-dropdown__task-count">
                    {taskCount}
                  </span>
                  
                  {isBlocked ? (
                    <span className="column-limits-dropdown__no-limit" title="Blocked columns cannot have WIP limits">
                      —
                    </span>
                  ) : isEditing ? (
                    <div className="column-limits-dropdown__edit">
                      <input
                        ref={el => inputRefs.current[status.id] = el}
                        type="number"
                        min="0"
                        className="column-limits-dropdown__input"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, status.id)}
                        onBlur={() => handleSave(status.id)}
                        placeholder="∞"
                      />
                    </div>
                  ) : (
                    <button
                      className={`column-limits-dropdown__limit ${hasLimit ? 'column-limits-dropdown__limit--set' : ''} ${isAtLimit ? 'column-limits-dropdown__limit--at' : ''} ${isOverLimit ? 'column-limits-dropdown__limit--over' : ''}`}
                      onClick={() => handleStartEdit(status.id, status.wipLimit)}
                      title={hasLimit ? 'Click to edit limit' : 'Click to set limit'}
                    >
                      {hasLimit ? (
                        <>
                          <span className="column-limits-dropdown__limit-value">{status.wipLimit}</span>
                          <button 
                            className="column-limits-dropdown__clear"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClear(status.id);
                            }}
                            title="Remove limit"
                          >
                            ×
                          </button>
                        </>
                      ) : (
                        <span className="column-limits-dropdown__limit-placeholder">Set</span>
                      )}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <div className="column-limits-dropdown__footer">
            <span className="column-limits-dropdown__hint">
              Click a limit to edit • Blocked columns are exempt
            </span>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

// Export with both names for backwards compatibility
export { StatusLimitsDropdown };
export default StatusLimitsDropdown;

// Alias for backwards compatibility
export const ColumnLimitsDropdown = StatusLimitsDropdown;

